import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Tent, Store, BadgeCheck, Zap, Droplet, ArrowRight, Facebook, Instagram, Twitter, Youtube, HelpCircle, Users, Clock, ShieldCheck, CheckCircle2, ChevronRight, Globe, MousePointer2, LayoutDashboard, Image as ImageIcon, MapPin, TrendingUp, Sparkles, LogIn, Gift } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { AuthModal } from '../components/AuthModal';
import { useAuth } from '../contexts/AuthContext';

// `text` affiche une valeur statique (ex: "Bêta ouverte") au lieu d'animer un nombre —
// utilisé pour les statistiques qui ne sont pas des métriques chiffrées.
const Counter = ({ value, suffix = "", label, text }: { value?: number; suffix?: string; label: string; text?: string }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (text !== undefined || value === undefined) return;
    let start = 0;
    const end = value;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, text]);

  return (
    <div className="text-center group">
      <div className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-2 group-hover:text-[#4b3cfa] transition-colors duration-500">
        {text !== undefined ? text : <>{count.toLocaleString()}{suffix}</>}
      </div>
      <div className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-gray-500 group-hover:text-gray-300 transition-colors">{label}</div>
    </div>
  );
};

export default function ExhibitorLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, userRole } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');

  const handleLogin = (mode: 'login' | 'signup' = 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // "Parraine ton exposant" : un exposant déjà connecté va directement sur sa
  // page de parrainage ; un visiteur non connecté doit d'abord créer un compte
  // (la route /stand/parrainage est protégée et renvoie sinon vers la landing orga).
  const handleParrainageClick = () => {
    if (user && userRole === 'stand') {
      navigate('/stand/parrainage');
    } else {
      handleLogin('signup');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col font-sans text-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-6 py-4 md:px-10 h-20">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
          <Logo className="text-2xl md:text-3xl" />
        </div>
        
        <nav className="hidden lg:flex items-center space-x-10">
          <button onClick={() => navigate('/')} className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all pb-1 border-b-2 border-transparent">{t('landing.navOrganizers')}</button>
          <button className="text-[10px] font-black uppercase tracking-[0.3em] text-[#4b3cfa] border-b-2 border-[#4b3cfa] pb-1">{t('landing.navExhibitors')}</button>
          <button onClick={handleParrainageClick} className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all pb-1 border-b-2 border-transparent">{t('userMenu.referExhibitor')}</button>
        </nav>

        <div className="flex items-center space-x-6">
          <button
            onClick={() => handleLogin('login')}
            className="hidden md:flex bg-[#4b3cfa] hover:bg-[#5a4bfc] text-white font-black py-2 px-6 rounded-full text-xs uppercase tracking-widest transition-all shadow-xl shadow-[#4b3cfa]/20 active:scale-95"
          >
            {t('landing.login')}
          </button>

          {/* Mobile Emoticons/Icons */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={() => navigate('/')}
              className={`p-3 transition-colors ${location.pathname === '/' ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-white'}`}
              aria-label={t('landing.navOrganizers') as string}
            >
              <Tent className="w-6 h-6" />
            </button>
            <button
              onClick={() => navigate('/exposants')}
              className={`p-3 transition-colors ${location.pathname === '/exposants' ? 'text-[#4b3cfa]' : 'text-gray-500 hover:text-white'}`}
              aria-label={t('landing.navExhibitors') as string}
            >
              <Store className="w-6 h-6" />
            </button>
            <button
              onClick={handleParrainageClick}
              className="p-3 transition-colors text-gray-500 hover:text-white"
              aria-label={t('userMenu.referExhibitor') as string}
            >
              <Gift className="w-6 h-6" />
            </button>
            <button
              onClick={() => handleLogin('login')}
              className="ml-2 text-white bg-white/5 p-3 rounded-full border border-white/10 active:scale-95 transition-all"
              aria-label={t('landing.login') as string}
            >
              <LogIn className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full pt-20">
        {/* HERO SECTION */}
        <section className="relative w-full min-h-[95vh] flex flex-col justify-center px-6 md:px-10 lg:px-20 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center z-0"
            style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?q=80&w=2000&auto=format&fit=crop")' }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent z-[1] opacity-70"></div>
          
          <div className="absolute top-1/4 right-[10%] w-[500px] h-[500px] bg-[#4b3cfa]/20 rounded-full blur-[120px] pointer-events-none animate-pulse"></div>
          
          <div className="relative z-10 max-w-[1400px] mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="max-w-5xl"
            >
              <div className="inline-flex items-center space-x-2 px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[10px] font-black uppercase tracking-[0.3em] text-white mb-10 shadow-2xl">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4b3cfa] animate-ping mr-2"></div>
                {t('landing.badge')}
              </div>

              <h1 className="text-4xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[0.9] mb-6 md:mb-10">
                {t('exhibitorLanding.heroTitle1')} <br className="hidden md:block" />
                {t('exhibitorLanding.heroTitle2')} <br className="hidden md:block" />
                <span className="text-[#4b3cfa] drop-shadow-[0_0_30px_rgba(75,60,250,0.3)]">{t('landing.heroFestivals')}</span> {t('exhibitorLanding.heroTitle3')}
              </h1>

              <p className="text-base md:text-2xl text-gray-400 font-medium leading-relaxed max-w-2xl mb-8 md:mb-12 drop-shadow-lg">
                {t('exhibitorLanding.heroSubtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-8">
                <button
                  onClick={() => handleLogin('signup')}
                  className="w-full sm:w-auto bg-[#4b3cfa] hover:bg-[#5a4bfc] text-white font-black py-4 md:py-6 px-10 md:px-14 rounded-2xl text-base md:text-lg transition-all shadow-[0_20px_50px_rgba(75,60,250,0.4)] flex items-center justify-center group active:scale-95"
                >
                  {t('exhibitorLanding.heroCta')} 🎪
                  <ArrowRight className="ml-3 w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="w-full sm:w-auto bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 text-white font-black py-4 md:py-6 px-10 md:px-14 rounded-2xl text-base md:text-lg transition-all flex items-center justify-center active:scale-95"
                >
                  {t('exhibitorLanding.howItWorksCta')}
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* STATS */}
        <section className="py-16 md:py-24 relative bg-white/[0.01] border-y border-white/5 overflow-hidden">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-32">
              <Counter text={t('exhibitorLanding.statStandsValue')} label={t('exhibitorLanding.statStands')} />
              <Counter value={29} suffix="€" label={t('exhibitorLanding.statCalls')} />
              <Counter value={100} suffix="%" label={t('exhibitorLanding.statCompliant')} />
            </div>
          </div>
        </section>

        {/* HOW IT WORKS FOR STANDS */}
        <section className="py-16 md:py-40 px-6 md:px-10 lg:px-20 relative">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-32 gap-6 md:gap-12">
              <div className="max-w-2xl">
                <div className="text-[#4b3cfa] font-black uppercase tracking-[0.3em] text-[10px] md:text-xs mb-4 md:mb-6">{t('exhibitorLanding.processLabel')}</div>
                <h2 className="text-3xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tighter">{t('exhibitorLanding.processTitle1')} <br /> {t('exhibitorLanding.processTitle2')}</h2>
              </div>
              <p className="text-lg md:text-xl text-gray-500 max-w-sm mb-4 font-medium leading-relaxed">
                {t('exhibitorLanding.processSubtitle')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
              {[
                {
                  step: 1,
                  title: t('exhibitorLanding.step1Title'),
                  desc: t('exhibitorLanding.step1Desc'),
                  icon: Sparkles,
                  color: "from-blue-500/20 to-blue-600/5"
                },
                {
                  step: 2,
                  title: t('exhibitorLanding.step2Title'),
                  desc: t('exhibitorLanding.step2Desc'),
                  icon: ShieldCheck,
                  color: "from-[#4b3cfa]/20 to-[#4b3cfa]/5"
                },
                {
                  step: 3,
                  title: t('exhibitorLanding.step3Title'),
                  desc: t('exhibitorLanding.step3Desc'),
                  icon: Zap,
                  color: "from-emerald-500/20 to-emerald-600/5"
                }
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/[0.03] rounded-3xl md:rounded-[3.5rem] p-8 md:p-12 border border-white/10 relative overflow-hidden group shadow-2xl h-full flex flex-col"
                >
                  <div className={`absolute -top-12 -right-12 w-48 h-48 bg-gradient-to-br ${item.color} rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity`}></div>
                  <div className="flex items-center space-x-4 md:space-x-6 mb-8 md:mb-12">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white font-black text-xl md:text-2xl">
                      {item.step}
                    </div>
                    <div className="h-px flex-1 bg-white/10"></div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-black mb-4 md:mb-6 leading-tight">{item.title}</h3>
                    <p className="text-sm md:text-base text-gray-500 leading-relaxed font-medium">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* BENEFITS FOR STANDS */}
        <section className="py-16 md:py-40 px-6 md:px-10 lg:px-20 bg-white/[0.01] border-y border-white/5">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-24 items-center">
            <div>
              <div className="text-[#4b3cfa] font-black uppercase tracking-[0.3em] text-[10px] md:text-xs mb-6 md:mb-8 text-center lg:text-left">{t('exhibitorLanding.benefitsLabel')}</div>
              <h2 className="text-3xl md:text-6xl font-black mb-8 md:mb-12 tracking-tighter leading-tight italic text-center lg:text-left">
                {t('exhibitorLanding.benefitsTitle1')} <br /> {t('exhibitorLanding.benefitsTitle2')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40">{t('exhibitorLanding.benefitsTitle3')}</span>
              </h2>

              <div className="grid grid-cols-1 gap-8 md:gap-12">
                {[
                  { icon: Clock, title: t('exhibitorLanding.adv1Title'), desc: t('exhibitorLanding.adv1Desc') },
                  { icon: MapPin, title: t('exhibitorLanding.adv2Title'), desc: t('exhibitorLanding.adv2Desc') },
                  { icon: TrendingUp, title: t('exhibitorLanding.adv3Title'), desc: t('exhibitorLanding.adv3Desc') }
                ].map((adv, i) => (
                  <div key={i} className="flex flex-col md:flex-row items-center md:items-start text-center md:text-left space-y-4 md:space-y-0 md:space-x-8 group cursor-default">
                    <div className="w-12 md:w-14 h-12 md:h-14 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 group-hover:border-[#4b3cfa]/30 transition-all shrink-0">
                      <adv.icon className="w-6 h-6 text-[#4b3cfa]" />
                    </div>
                    <div>
                      <h4 className="text-xl md:text-2xl font-black mb-2 md:mb-3 group-hover:text-white transition-colors">{adv.title}</h4>
                      <p className="text-sm md:text-base text-gray-500 font-medium leading-relaxed max-w-lg">{adv.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative group">
                <div className="absolute -inset-4 md:-inset-8 bg-gradient-to-r from-[#4b3cfa]/30 to-blue-500/30 rounded-3xl md:rounded-[4rem] blur-[60px] md:blur-[100px] opacity-40 animate-pulse"></div>
                <div className="relative bg-[#121212] border border-white/10 rounded-3xl md:rounded-[3.5rem] p-3 md:p-4 overflow-hidden shadow-2xl transform md:-rotate-1 hover:rotate-0 transition-transform duration-700">
                  <div className="bg-[#050505] rounded-[2rem] md:rounded-[3rem] p-6 md:p-12">
                    <div className="flex items-center justify-between mb-8 md:mb-12">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-[#4b3cfa] flex items-center justify-center">
                          <Store className="w-3 h-3 md:w-4 md:h-4 text-white" />
                        </div>
                        <Logo className="h-4 md:h-6" />
                      </div>
                      <div className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-[#4b3cfa] bg-[#4b3cfa]/10 px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-[#4b3cfa]/20">{t('exhibitorLanding.exhibitorSpace')}</div>
                    </div>

                    <div className="text-sm md:text-lg font-black text-white mb-5 md:mb-8">{t('exhibitorLanding.mockupTitle')}</div>

                    <div className="space-y-2 md:space-y-3">
                      {[
                        { name: 'Eurofood Festival', city: 'Paris', status: t('exhibitorLanding.mockupPending'), color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
                        { name: 'Vibrations Sud', city: 'Marseille', status: t('exhibitorLanding.mockupPaymentDue'), color: 'text-[#ff5c00] bg-[#ff5c00]/10 border-[#ff5c00]/20' },
                        { name: 'Marché de Noël', city: 'Lyon', status: t('exhibitorLanding.mockupConfirmed'), color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
                      ].map((row) => (
                        <div key={row.name} className="flex items-center justify-between gap-2 py-3 md:py-4 px-3 md:px-5 bg-white/5 rounded-xl md:rounded-2xl border border-white/5">
                          <div className="min-w-0">
                            <div className="text-[11px] md:text-sm font-bold text-white truncate">{row.name}</div>
                            <div className="text-[9px] md:text-[11px] font-bold uppercase tracking-wider text-[#4b3cfa]/70 mt-0.5">{row.city}</div>
                          </div>
                          <div className={`shrink-0 text-[8px] md:text-[10px] font-black uppercase tracking-wider px-2 md:px-3 py-1 md:py-1.5 rounded-full border whitespace-nowrap ${row.color}`}>{row.status}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-24 md:py-48 px-6 relative overflow-hidden bg-black">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-b from-[#4b3cfa]/10 to-transparent pointer-events-none opacity-40"></div>
          
          <div className="max-w-7xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-7xl lg:text-8xl font-black mb-8 md:mb-16 tracking-tighter leading-none italic uppercase">
              {t('exhibitorLanding.finalCtaTitle1')} <br /> <span className="text-[#4b3cfa] drop-shadow-[0_0_50px_rgba(75,60,250,0.5)]">{t('exhibitorLanding.finalCtaTitle2')}</span>
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-10">
              <button
                onClick={() => handleLogin('signup')}
                className="w-full sm:w-auto bg-[#4b3cfa] hover:bg-[#5a4bfc] text-white font-black py-5 md:py-8 px-10 md:px-20 rounded-2xl md:rounded-[2.5rem] text-xl md:text-2xl transition-all shadow-[0_20px_80px_rgba(75,60,250,0.4)] active:scale-95 flex items-center justify-center group"
              >
                {t('exhibitorLanding.joinPlatform')}
                <ArrowRight className="ml-4 w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        initialMode={authMode} 
        role="stand" 
      />
    </div>
  );
}
